import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiEye, FiPrinter, FiX } from 'react-icons/fi';
import api, { getCashDesks, getCashRegisterHistory, getCashRegisterSummary, getCashRegisterMovements } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { imprimirCaja } from './AperturaCajas';
import '../../../styles/AperturaCajas.css';

const fmtMoney = (n) => parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate  = (ts) => ts ? new Date(ts).toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Lima' }) : '—';
const fmtHora  = (ts) => ts ? new Date(ts).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Lima' }) : '—';
const today    = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const firstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

/* ── Modal de detalle del cuadre ──────────────────────────────────────────── */
function CuadreModal({ registerId, onClose }) {
  const [data, setData] = useState(null);
  const [movements, setMovements] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      getCashRegisterSummary(registerId),
      getCashRegisterMovements(registerId),
    ]).then(([sumRes, movRes]) => {
      setData(sumRes.data.data);
      setMovements(movRes.data.data || []);
    }).catch(e => setError(e.response?.data?.error || e.message));
  }, [registerId]);

  return (
    <div className="caj-overlay" onClick={onClose}>
      <div className="caj-modal caj-modal-cuadre" onClick={e => e.stopPropagation()}>
        <div className="caj-modal-header caj-modal-header-info">
          <span>Detalle del Cuadre</span>
          <button className="caj-modal-close" onClick={onClose}><FiX size={18} /></button>
        </div>
        <div className="caj-modal-body">
          {error ? (
            <div style={{ color: '#dc2626', fontSize: 12.5 }}>{error}</div>
          ) : !data ? (
            <div style={{ textAlign: 'center', color: '#94a3b8', padding: 30 }}>Cargando...</div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: '#1e293b' }}>{data.register.warehouse_name}</div>
                  <div style={{ fontSize: 11.5, color: '#64748b' }}>
                    {data.register.full_name || data.register.username} · {fmtDate(data.register.opened_at)}
                  </div>
                </div>
                <span className={`caj-badge-estado ${data.register.status === 'open' ? 'caj-badge-open' : 'caj-badge-closed'}`}>
                  {data.register.status === 'open' ? 'ABIERTA' : 'CERRADA'}
                </span>
              </div>

              <div style={{ fontSize: 11.5, color: '#64748b', marginBottom: 12 }}>
                Apertura: <strong>{fmtHora(data.register.opened_at)}</strong>
                <span style={{ margin: '0 8px', color: '#cbd5e1' }}>→</span>
                Cierre: <strong>{data.register.closed_at ? fmtHora(data.register.closed_at) : '—'}</strong>
              </div>

              {/* Ventas por forma de pago */}
              <div className="caj-section-title">Ventas de la sesión</div>
              {data.ventas.filter(v => v.total > 0).map(v => (
                <div key={v.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: 12.5 }}>
                  <span>{v.name}</span>
                  <span style={{ fontWeight: 600 }}>S/ {fmtMoney(v.total)}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 8px', fontSize: 13, fontWeight: 800, borderTop: '1px solid #e2e8f0', color: '#1d4ed8' }}>
                <span>Total Ventas</span>
                <span>S/ {fmtMoney(data.ventas_total)}</span>
              </div>
              {parseFloat(data.ventas_anuladas || 0) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: 11.5, color: '#dc2626' }}>
                  <span>Ventas anuladas</span>
                  <span>S/ {fmtMoney(data.ventas_anuladas)}</span>
                </div>
              )}

              {/* Cobros CxC */}
              {data.cobros.length > 0 && (
                <>
                  <div className="caj-section-title" style={{ marginTop: 10 }}>Cobro de créditos</div>
                  {data.cobros.map(c => (
                    <div key={c.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: 12.5 }}>
                      <span>{c.name}</span>
                      <span style={{ fontWeight: 600 }}>S/ {fmtMoney(c.total)}</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: 12.5, fontWeight: 700 }}>
                    <span>Total Cobrado</span>
                    <span>S/ {fmtMoney(data.cobros_total)}</span>
                  </div>
                </>
              )}

              {/* Movimientos de caja: manuales + cobros de crédito */}
              {movements.length > 0 && (
                <>
                  <div className="caj-section-title" style={{ marginTop: 10 }}>Movimientos de caja</div>
                  <table className="caj-table caj-table-compact">
                    <thead>
                      <tr><th>Hora</th><th>Tipo</th><th>Tipo Ingreso</th><th>Descripción</th><th style={{ textAlign: 'right' }}>Monto</th></tr>
                    </thead>
                    <tbody>
                      {movements.map(m => {
                        const isCobro = m.reference_type === 'ar_payment';
                        return (
                          <tr key={m.id}>
                            <td className="caj-td-date">{fmtHora(m.movement_date)}</td>
                            <td>
                              <span style={{
                                padding: '1px 7px', borderRadius: 8, fontSize: 10.5, fontWeight: 700,
                                background: m.movement_type === 'income' ? '#dcfce7' : '#fee2e2',
                                color:      m.movement_type === 'income' ? '#166534' : '#991b1b',
                              }}>{m.movement_type === 'income' ? 'INGRESO' : 'EGRESO'}</span>
                            </td>
                            <td>
                              {m.category && (
                                <span style={{
                                  padding: '1px 7px', borderRadius: 8, fontSize: 10.5, fontWeight: 700,
                                  background: isCobro ? '#ede9fe' : '#f1f5f9',
                                  color:      isCobro ? '#6d28d9' : '#64748b',
                                }}>{isCobro ? `COBRO · ${m.category}` : m.category}</span>
                              )}
                            </td>
                            <td>{m.description || '—'}</td>
                            <td style={{ textAlign: 'right', fontWeight: 700, color: m.movement_type === 'income' ? '#16a34a' : '#dc2626' }}>
                              {m.movement_type === 'income' ? '+' : '-'} S/ {fmtMoney(m.amount)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}

              {/* Cuadre de efectivo */}
              <div className="caj-section-title" style={{ marginTop: 10 }}>Cuadre de efectivo</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: 12.5 }}>
                <span>+ Monto apertura</span><span>S/ {fmtMoney(data.movimientos.open_amount)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: 12.5, color: '#16a34a' }}>
                <span>+ Ventas efectivo</span><span>S/ {fmtMoney(data.movimientos.ventas_efectivo)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: 12.5, color: '#16a34a' }}>
                <span>+ Cobro créditos efectivo</span><span>S/ {fmtMoney(data.movimientos.cobro_efectivo)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: 12.5, color: '#16a34a' }}>
                <span>+ Ingresos efectivo</span><span>S/ {fmtMoney(data.movimientos.ingreso_efectivo)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: 12.5, color: '#dc2626' }}>
                <span>- Egresos</span><span>S/ {fmtMoney(data.movimientos.salida_efectivo)}</span>
              </div>
              <div style={{
                display: 'flex', justifyContent: 'space-between', padding: '10px 8px', marginTop: 6,
                background: '#f8fafc', borderRadius: 8, fontWeight: 800, fontSize: 14
              }}>
                <span>Total esperado en caja</span>
                <span>S/ {fmtMoney(data.movimientos.total_efectivo)}</span>
              </div>
              <div style={{
                display: 'flex', justifyContent: 'space-between', padding: '8px 8px', marginTop: 4,
                fontWeight: 700, fontSize: 13, color: '#2563eb'
              }}>
                <span>Total Ingresos</span>
                <span>S/ {fmtMoney(data.movimientos.total_ingresos)}</span>
              </div>

              {data.register.status === 'closed' && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 8px 2px', fontSize: 12.5 }}>
                    <span>Monto declarado al cierre</span>
                    <span>S/ {fmtMoney(data.register.close_amount)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: 13, fontWeight: 700 }}>
                    <span>Diferencia</span>
                    <span style={{ color: parseFloat(data.diferencia) > 0 ? '#16a34a' : parseFloat(data.diferencia) < 0 ? '#dc2626' : '#64748b' }}>
                      {parseFloat(data.diferencia) >= 0 ? '+' : ''}S/ {fmtMoney(data.diferencia)}
                    </span>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <div className="caj-modal-footer">
          <button className="caj-btn caj-btn-primary" onClick={() => data && imprimirCaja(data)} disabled={!data}>
            <FiPrinter size={13} /> Imprimir
          </button>
          <button className="caj-btn caj-btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

/* ── Página principal ──────────────────────────────────────────────────────── */
export default function HistorialCuadres({ businessId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [rows, setRows]           = useState([]);
  const [cajas, setCajas]         = useState([]);
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [modalId, setModalId]     = useState(null);

  const [filters, setFilters] = useState({
    from_date: firstOfMonth(),
    to_date:   today(),
    caja_id: '',
    user_id: '',
  });

  useEffect(() => {
    getCashDesks().then(r => setCajas(r.data.data || [])).catch(() => {});
    // El filtro por usuario solo tiene sentido (y solo lo ve) el administrador —
    // un usuario normal ya solo recibe su propio historial del backend.
    if (isAdmin) {
      api.get('/api/users').then(r => setUsers(r.data.data || [])).catch(() => {});
    }
  }, [isAdmin]);

  const search = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await getCashRegisterHistory({
        from_date: filters.from_date, to_date: filters.to_date,
        caja_id: filters.caja_id,
        user_id: isAdmin ? filters.user_id : '',
        limit: 200,
      });
      setRows(res.data.rows || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }, [filters, isAdmin]);

  useEffect(() => { search(); }, [search]);

  return (
    <div className="caj-page">
      <div className="caj-filter-bar">
        <span className="caj-filter-label">Desde:</span>
        <input type="date" className="caj-filter-input" value={filters.from_date}
          onChange={e => setFilters(p => ({ ...p, from_date: e.target.value }))} />
        <span className="caj-filter-sep">—</span>
        <span className="caj-filter-label">Hasta:</span>
        <input type="date" className="caj-filter-input" value={filters.to_date}
          onChange={e => setFilters(p => ({ ...p, to_date: e.target.value }))} />
        <span className="caj-filter-label">Caja:</span>
        <select className="caj-filter-input" value={filters.caja_id}
          onChange={e => setFilters(p => ({ ...p, caja_id: e.target.value }))}>
          <option value="">Todas</option>
          {cajas.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {isAdmin && (
          <>
            <span className="caj-filter-label">Usuario:</span>
            <select className="caj-filter-input" value={filters.user_id}
              onChange={e => setFilters(p => ({ ...p, user_id: e.target.value }))}>
              <option value="">Todos</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}
            </select>
          </>
        )}
        <button className="caj-btn caj-btn-primary" onClick={search}>
          <FiSearch size={12} /> Buscar
        </button>
        <span className="caj-badge-count" style={{ marginLeft: 'auto' }}>
          {rows.length} cuadre(s)
        </span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 30, color: '#94a3b8' }}>Cargando...</div>
        ) : error ? (
          <div style={{ color: '#dc2626', padding: 20 }}>{error}</div>
        ) : (
          <table className="caj-table">
            <thead>
              <tr>
                <th>Caja</th>
                <th>Almacén</th>
                <th>Cajero</th>
                <th>Fecha</th>
                <th>Apertura</th>
                <th>Cierre</th>
                <th style={{ textAlign: 'right' }}>Mto. Apertura</th>
                <th style={{ textAlign: 'right' }}>Vtas. Efectivo</th>
                <th style={{ textAlign: 'right' }}>Vtas. Total</th>
                <th style={{ textAlign: 'right' }}>Ingresos</th>
                <th style={{ textAlign: 'right' }}>Egresos</th>
                <th style={{ textAlign: 'right' }}>Total Efect.</th>
                <th style={{ textAlign: 'right' }}>Mto. Cierre</th>
                <th style={{ textAlign: 'right' }}>Diferencia</th>
                <th style={{ textAlign: 'center' }}>Estado</th>
                <th style={{ textAlign: 'center' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={16} style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>
                  Sin cuadres en el período seleccionado
                </td></tr>
              ) : rows.map(row => {
                const dif = row.difference != null ? parseFloat(row.difference) : null;
                return (
                  <tr key={row.id}>
                    <td style={{ fontWeight: 700 }}>{row.caja_name || '—'}</td>
                    <td>{row.warehouse_name}</td>
                    <td style={{ fontSize: 11.5, color: '#64748b' }}>{row.full_name || row.username}</td>
                    <td>{fmtDate(row.opened_at)}</td>
                    <td style={{ fontSize: 11.5, color: '#64748b' }}>{fmtHora(row.opened_at)}</td>
                    <td style={{ fontSize: 11.5, color: '#64748b' }}>{row.closed_at ? fmtHora(row.closed_at) : '—'}</td>
                    <td style={{ textAlign: 'right' }}>S/ {fmtMoney(row.open_amount)}</td>
                    <td style={{ textAlign: 'right', color: '#16a34a' }}>S/ {fmtMoney(row.total_ventas_efectivo)}</td>
                    <td style={{ textAlign: 'right', color: '#2563eb' }}>S/ {fmtMoney(row.total_ventas)}</td>
                    <td style={{ textAlign: 'right', color: '#16a34a' }}>S/ {fmtMoney(row.total_ingresos)}</td>
                    <td style={{ textAlign: 'right', color: '#dc2626' }}>S/ {fmtMoney(row.total_egresos)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>S/ {fmtMoney(row.total_efectivo_esperado)}</td>
                    <td style={{ textAlign: 'right' }}>{row.close_amount != null ? `S/ ${fmtMoney(row.close_amount)}` : '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      {dif != null ? (
                        <span style={{ color: dif > 0 ? '#16a34a' : dif < 0 ? '#dc2626' : '#64748b', fontWeight: 700 }}>
                          {dif >= 0 ? '+' : ''}S/ {fmtMoney(dif)}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`caj-badge-estado ${row.status === 'open' ? 'caj-badge-open' : 'caj-badge-closed'}`}>
                        {row.status === 'open' ? 'ABIERTA' : 'CERRADA'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button className="caj-btn caj-btn-secondary" style={{ padding: '4px 10px', fontSize: 11 }}
                        onClick={() => setModalId(row.id)}>
                        <FiEye size={12} /> Ver
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {modalId && <CuadreModal registerId={modalId} onClose={() => setModalId(null)} />}
    </div>
  );
}
